// src/pages/AnalyzingPage.tsx
import React, { useEffect } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalization } from "../hooks/useLocalization";
import { HaloAnimation } from "../components/HaloAnimation";
import { useResultData } from "../hooks/useResultData";
import { API_URL } from "../config";

type ContentCheckerResponse = {
  content_checker_id?: number;
};

const Page = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100svh;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  z-index: 10;
  text-align: center;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #e8eefc;
  letter-spacing: 0.02em;
  margin-bottom: 18px;

  background: linear-gradient(
    90deg,
    #757ea7 -8.1%,
    #ffffff 53.63%,
    #3e466d 108.63%
  );
  -webkit-text-fill-color: transparent;
  background-clip: text;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 42px;
    line-height: 62px;
  }
`;

const Sub = styled.div`
  margin-top: 18px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
  }
`;

type State =
  | { mode: "image"; dataUrl: string }
  | { mode: "phone"; phone: string };

/** 文字轉圖片（與你原本一致） */
const textToImage = async (txt: string) => {
  const font =
    "bold 36px -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif";
  const padX = 24,
    padY = 24;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  ctx.font = font;
  const metrics = ctx.measureText(txt);
  const w = Math.ceil(metrics.width) + padX * 2;
  const h = 36 + padY * 2;
  canvas.width = Math.max(360, Math.min(800, w));
  canvas.height = Math.max(120, h);
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#111827";
  ctx.font = font;
  ctx.textBaseline = "middle";
  ctx.fillText(txt, padX, canvas.height / 2);
  const dataUrl = canvas.toDataURL("image/png");
  const base64 = dataUrl.replace(/^data:image\/png;base64,/, "");
  return { dataUrl, base64 };
};

export default function AnalyzingPage() {
  const nav = useNavigate();
  const { t } = useLocalization();
  const { state } = useLocation() as { state?: State };

  // 建立成功回傳的 content_checker_id
  const [jobId, setJobId] = React.useState<number | null>(null);

  // 用新的 hook：直接帶 id 進去
  const { data, loading, refreshing, refetch } = useResultData({
    id: jobId ?? undefined,
  });

  // 進頁先建立工作，成功後把 id 存到本頁 state
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        // 已有 id（例如返回本頁）就別重送
        if (jobId) return;

        if (!state) {
          nav(-1);
          return;
        }

        let base64: string | undefined;
        let dataUrlForFallback: string | undefined;

        if (state.mode === "image") {
          dataUrlForFallback = state.dataUrl;
          base64 = state.dataUrl.replace(
            /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
            ""
          );
        } else {
          const { dataUrl, base64: b64 } = await textToImage(
            state.phone.trim()
          );
          dataUrlForFallback = dataUrl;
          base64 = b64;
        }

        const resp = await fetch(`${API_URL}/content-checker`, {
          method: "POST",
          body: JSON.stringify({ region: "TW", image: base64 }),
        });

        if (!resp.ok) {
          // 建立失敗 → 用兜底把圖帶去結果頁
          const imgParam = dataUrlForFallback
            ? encodeURIComponent(
                dataUrlForFallback.replace(
                  /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
                  ""
                )
              )
            : "";
          if (!cancelled) nav(`/result${imgParam ? `?img=${imgParam}` : ""}`);
          return;
        }

        const json: ContentCheckerResponse = await resp
          .json()
          .catch(() => ({}));
        const newId = json?.content_checker_id;

        if (!cancelled && typeof newId === "number") {
          setJobId(newId);
        } else if (!cancelled) {
          // 拿不到 id 也走兜底
          const imgParam = dataUrlForFallback
            ? encodeURIComponent(
                dataUrlForFallback.replace(
                  /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
                  ""
                )
              )
            : "";
          nav(`/result${imgParam ? `?img=${imgParam}` : ""}`);
        }
      } catch {
        if (!cancelled) nav(`/result`);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [jobId, nav, state]);

  // 輪詢：當有 jobId 且狀態為 0/1 時定時 refetch，直到 2/3 導頁
  const pollTimerRef = React.useRef<number | null>(null);
  const attemptsRef = React.useRef(0);
  const MAX_ATTEMPTS = 40; // ~2 分鐘

  useEffect(() => {
    // 沒 id → 不輪詢
    if (!jobId) return;

    const status = data?.status as 0 | 1 | 2 | 3 | undefined;

    // 若已完成或失敗 → 直接導去結果頁（把 id 帶到 URL，沿用現有 ResultPage）
    if (status === 2 || status === 3) {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      attemptsRef.current = 0;
      nav(`/result?id=${encodeURIComponent(String(jobId))}`);
      return;
    }

    // 需要持續輪詢（只有 0 或 1）
    if (pollTimerRef.current) {
      // 已經有排程就不重建，由下一輪觸發
      return;
    }

    pollTimerRef.current = window.setTimeout(async () => {
      pollTimerRef.current = null; // 消費一次後釋放

      // 守門：避免重複打
      if (!refreshing && !loading) {
        attemptsRef.current += 1;

        if (attemptsRef.current > MAX_ATTEMPTS) {
          // 超時：直接導去結果頁，讓使用者查看目前拿到的狀態/或顯示逾時資訊
          nav(`/result?id=${encodeURIComponent(String(jobId))}`);
          return;
        }

        // 靜默刷新
        refetch(true);
      }
    }, 3000) as unknown as number;

    // 清理：離開頁面或依賴變動時清掉排程
    return () => {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [jobId, data?.status, loading, refreshing, refetch, nav]);

  // 顯示 ResultPage 原本的 banner 文案，但搬到分析頁
  const status = data?.status as 0 | 1 | 2 | 3 | undefined;
  const bannerText =
    status === 0
      ? t("request_sent_to_ai")
      : status === 1
      ? t("ai_analyzing")
      : null;

  // useEffect(() =>
  // {
  //     setOptions({
  //         bgTop: 250,        // 讓背景整塊往上偏 40px
  //         footerFixed: true, // 固定 footer
  //         overflowY: "hidden"
  //     });
  // }, [setOptions]);

  return (
    <Page>
      <Title>{t("analyzing_title")}</Title>
      <HaloAnimation />
      <Sub>
        {/* 1) 有狀態就顯示對應文案（0/1） */}
        {bannerText ||
          // 2) 還沒拿到 id 時顯示「資料確認中」
          (!jobId ? t("data_confirmation") : t("ai_analyzing"))}
        {(loading || refreshing) && <span style={{ opacity: 0.8 }}> …</span>}
      </Sub>
    </Page>
  );
}
